// src/pages/admin/Dashboard1.js
import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectToken } from "../../auth/authSelectors";
import dashboardApi from "../../api/admin/dashboard";

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { Spinner } from "react-bootstrap";
import "./Dashboard1.css"; // 👈 Add this import

const COLORS = [
  "#007bff", "#28a745", "#17a2b8", "#ffc107", "#dc3545",
  "#6c757d", "#6f42c1", "#e83e8c", "#fd7e14", "#20c997"
];

export default function Dashboard1() {
  const token = useSelector(selectToken);
  const [chartData, setChartData] = useState([]);
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);

  const authData = useMemo(() => ({ token }), [token]);

  const getSportIcon = (sport) => ({
    "Chess": "fas fa-chess-knight",
    "Music": "fas fa-music",
    "Spoken English": "fas fa-volume-up",
    "Art & Painting": "fas fa-paint-brush",
    "Dance": "fas fa-compact-disc",
    "Vocal Music": "fas fa-microphone-alt",
    "Photography": "fas fa-camera",
    "Poetry": "fas fa-feather-alt",
    "Swimming": "fas fa-swimmer",
    "Golf": "fas fa-golf-ball"
  }[sport] || "fas fa-star");

  useEffect(() => {
    if (!authData.token) {
      setError("Authentication Failed");
      setLoading(false);
      return;
    }

    const fetchDashboard = async () => {
      try {
        const res = await dashboardApi.getDashboardData(authData.token);
        if (res.status !== "success") throw new Error("API Error");

        setSports(res.categories);

        const formatted = res.categories.map((sport) => ({
          id: sport.sport_id,
          name: sport.sport_name,
          achievedBlocks: sport.achieved_blocks,
          targetBlocks: sport.target_blocks,
          achievedStudents: sport.achieved_students,
          targetStudents: sport.target_students,
          remainingStudents: sport.remaining_students,
          percent: Number(sport.achievement_percent) || 0,
        }));

        setChartData(formatted);
      } catch (err) {
        console.error(err);
        setError("Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [authData]);

  const toggleExpand = (index) =>
    setExpandedIndex(expandedIndex === index ? null : index);

  return (
    <section className="content">
      <div className="container-fluid">

        {/* Header */}
        <div className="mb-5 pt-3 text-center">
          <h2 className="fw-bold text-dark">Katral Kondattam Dashboard</h2>
          <p className="text-muted small">Activity Block & Student Performance Overview</p>
        </div>

        {loading && (
          <div className="text-center my-4">
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        {error && !loading && (
          <div className="alert alert-danger text-center small">{error}</div>
        )}

        {!loading && !error && (
          <>
            {/* Category Cards */}
            <div className="row">
              {chartData.map((cat, i) => {
                const color = COLORS[i % COLORS.length];
                const expanded = expandedIndex === i;
                const blocks = sports[i]?.blocks || [];
                const progressColor =
                  cat.percent >= 75 ? "bg-success"
                    : cat.percent >= 50 ? "bg-warning"
                      : "bg-danger";

                return (
                  <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4" key={i}>
                    <div className="card shadow h-100"
                      style={{ borderLeft: `4px solid ${color}` }}>

                      <div className="card-header bg-white border-0 py-2 d-flex align-items-center">
                        <i className={`${getSportIcon(cat.name)} me-2`}
                          style={{ color, fontSize: "1.3rem" }}></i>
                        <strong className="text-dark small">{cat.name}</strong>
                      </div>

                      <div className="card-body pt-0 pb-2 px-3">
                        <p className="small fw-bold mb-1 text-center">
                          Achievement: <span style={{ color }}>{cat.percent}%</span>
                        </p>
                        <div className="progress mb-3" style={{ height: "8px" }}>
                          <div className={`progress-bar ${progressColor}`}
                            style={{ width: `${cat.percent}%` }} />
                        </div>

                        <h6 className="d-flex justify-content-between text-primary">
                          <span>Blocks</span><span>{cat.achievedBlocks} / {cat.targetBlocks}</span>
                        </h6>
                        <h6 className="d-flex justify-content-between text-success">
                          <span>Students</span><span>{cat.achievedStudents} / {cat.targetStudents}</span>
                        </h6>
                        <h6 className="d-flex justify-content-between text-danger">
                          <span>Remaining</span><span>{cat.remainingStudents}</span>
                        </h6>

                        {!!blocks.length && (
                          <button
                            className="btn btn-sm btn-outline-primary w-100 mt-2"
                            onClick={() => toggleExpand(i)}
                          >
                            {expanded ? "Hide Blocks ▲" : "View Blocks ▼"}
                          </button>
                        )}
                      </div>

                      {/* ⭐ SINGLE RESPONSIVE EXPAND UI */}
                      {expanded && (
                        <div className="px-3 pb-3">
                          <div className="block-wrapper mt-2">
                            {blocks.map((b, idx) => (
                              <div
                                key={idx}
                                className="block-row"
                                style={{ borderLeft: `4px solid ${color}` }}
                              >
                                <div className="block-name" title={b.block_name}>
                                  {b.block_name}
                                </div>
                                <div className="block-students">
                                  {b.students_enrolled}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    </div>
                  </div>
                );
              })}
            </div>

            <hr />

            {/* Charts */}
            <div className="row">
              <div className="col-lg-7 mb-4">
                <div className="card shadow h-100">
                  <div className="card-header fw-bold">📊 Target vs Achievement</div>
                  <div className="card-body" style={{ height: "380px" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="targetStudents" fill="#adb5bd" name="Target" />
                        <Bar dataKey="achievedStudents" fill="#28a745" name="Achieved" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              <div className="col-lg-5 mb-4">
                <div className="card shadow h-100">
                  <div className="card-header fw-bold">🏆 Achievement % by Category</div>
                  <div className="card-body" style={{ height: "380px" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chartData}
                          dataKey="percent"
                          nameKey="name"
                          cx="45%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={110}
                          paddingAngle={3}
                          label={false}
                        >
                          {chartData.map((_, idx) => (
                            <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name, item) =>
                          [`${value}%`, item.payload.name]} />
                        <Legend layout="vertical" align="right" verticalAlign="middle" />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

            </div>

          </>
        )}

      </div>
    </section>
  );
}
